import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar, Card, Skeleton, SearchBar } from 'antd-mobile';
import { ArrowLeftOutlined, RightOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { getNavBarContainerStyle } from '../../../../utils/deviceUtils';
import { DeliveringPath } from '../../../../enums/DeliveryNavigatePathEnum';
import { getUserInfo } from '../../../../utils/auth';
import {
    GET_DRIVER_SMS_RECORDS_BY_DRIVER_ID,
    GetDriverSmsRecordsByDriverIdResponse,
    GetDriverSmsRecordsByDriverIdVariables,
    DriverSmsRecordItem
} from './graphql/queries/getDriverSmsRecordsByDriverId';
import { t } from 'i18n';

const ChatListPage: React.FC = () => {
    const navigate = useNavigate();
    const userInfo = getUserInfo();
    const driverId = useMemo(() => Number(userInfo?.account?.id ?? 0), [userInfo?.account?.id]);
    const driverName = userInfo?.account?.name ?? '';
    const [searchValue, setSearchValue] = useState('');

    const { data, loading, error } = useQuery<
        GetDriverSmsRecordsByDriverIdResponse,
        GetDriverSmsRecordsByDriverIdVariables
    >(GET_DRIVER_SMS_RECORDS_BY_DRIVER_ID, {
        variables: { driverId },
        skip: !driverId,
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
        pollInterval: 10000
    });

    const handleBack = () => {
        navigate(-1);
    };

    const handleChatClick = (record: DriverSmsRecordItem) => {
        navigate(DeliveringPath.DELIVERY_CHAT, {
            state: {
                recordId: record.id,
                stopNumber: record.stopNumber,
                subStopNumber: record.subStopNumber,
                fullName: record.customerName,
                tracking: record.trackingNumber,
                driverId: record.driverId ?? driverId,
                driverName:driverName,
                orderAddressId: record.recipientAddressId
            }
        });
    };

    const records = data?.getDriverSmsRecordsByDriverId ?? [];
    
    // 根据搜索值过滤记录
    const filteredRecords = useMemo(() => {
        if (!searchValue.trim()) {
            return records;
        }
        return records.filter(record => 
            record.trackingNumber.toLowerCase().includes(searchValue.toLowerCase())
        );
    }, [records, searchValue]);

    const renderContent = () => {

        if (loading) {
            return (
                <div className="px-4 py-4">
                    <Skeleton.Title animated />
                    <Skeleton.Paragraph lineCount={3} animated />
                    <Skeleton.Title animated />
                    <Skeleton.Paragraph lineCount={3} animated />
                </div>
            );
        }


        return (
            <div className="px-4 py-4 space-y-4">
                {filteredRecords.length === 0 ? (
                    <div className="flex items-center justify-center h-40 text-gray-500">
                        {t('pages.delivery.delivering.noMessages')}
                    </div>
                ) : (
                    filteredRecords.map((record: DriverSmsRecordItem) => {
                        const stopLabel = record.subStopNumber && record.subStopNumber !== '0'
                            ? `${t('pages.delivery.delivering.stop')} ${record.stopNumber}-${record.subStopNumber}`
                            : `${t('pages.delivery.delivering.stop')} ${record.stopNumber}`;
                        const unreadCount = record.isNotReadCount || 0;
                        
                        return (
                            <Card
                                key={record.id}
                                className="cursor-pointer relative"
                                onClick={() => handleChatClick(record)}
                            >
                                {unreadCount > 0 && (
                                    <div className="absolute top-2 right-2 flex items-center justify-center w-5 h-5 bg-red-500 rounded-full">
                                        <span className="text-xs text-white font-medium">
                                            {unreadCount > 99 ? '99+' : unreadCount}
                                        </span>
                                    </div>
                                )}
                                <div className="flex items-center justify-between">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-gray-500">{stopLabel}</div>
                                        <div className="text-base font-semibold text-gray-900 mt-0.5">
                                            {record.trackingNumber}
                                        </div>
                                        <div className="text-sm text-gray-700 mt-1">
                                            {record.customerName}
                                        </div>
                                    </div>
                                    <RightOutlined className="text-gray-400 ml-2 flex-shrink-0" />
                                </div>
                            </Card>
                        );
                    })
                )}
            </div>
        );
    };

    return (
        <div className="flex flex-col h-screen bg-white overflow-hidden">
            {/* 导航栏 */}
            <div {...getNavBarContainerStyle()} className="flex-shrink-0 bg-gray-50 border-b border-gray-200">
                <NavBar onBack={handleBack} backIcon={<ArrowLeftOutlined />}>
                    <span className="text-gray-800 font-medium">Messages</span>
                </NavBar>
            </div>

            {/* 搜索框 */}
            <div className="px-4 py-3 bg-white border-b border-gray-200">
                <SearchBar
                    placeholder={t('pages.delivery.delivering.searchContext')}
                    value={searchValue}
                    onChange={setSearchValue}
                />
            </div>

            {/* 聊天列表 */}
            <div className="flex-1 overflow-y-auto min-h-0 bg-gray-50">
                {renderContent()}
            </div>

        </div>
    );
};

export default ChatListPage;